import type { CSSProperties } from 'react';
import type { Project, Sphere } from '../lifeos/types.js';

/** 'AllAboard365' → 'AllAboard': the tab row has no room for the digits. */
export function shortJobName(name: string): string {
  return name.replace(/\s*\d+$/, '') || name;
}

/**
 * The job tabs (owner, 2026-10-13: "big tabs at the top"): All plus one per
 * active sphere, always visible, each in its sphere colour. `value` null = All.
 * Buttons with aria-pressed (not role=tab) so they never mix with a page's view tabs.
 */
export default function JobTabs({
  spheres,
  value,
  onChange,
  label = 'Job',
  allLabel = 'All',
}: {
  spheres: readonly Sphere[];
  value: string | null;
  onChange: (key: string | null) => void;
  label?: string;
  /** The null choice: 'All' as a filter, 'None' when picking a person's job. */
  allLabel?: string;
}): JSX.Element | null {
  if (spheres.length === 0) return null;
  const tab = (key: string | null, name: string, color: string | null): JSX.Element => {
    const on = value === key;
    return (
      <button
        key={key ?? 'all'}
        type="button"
        className={`jobtabs__btn${on ? ' jobtabs__btn--on' : ''}`}
        style={color ? ({ '--job': color } as CSSProperties) : undefined}
        aria-pressed={on}
        onClick={() => onChange(key)}
      >
        {color && <span className="jobtabs__dot" aria-hidden="true" />}
        {name}
      </button>
    );
  };
  return (
    <div className="jobtabs" role="group" aria-label={label}>
      {tab(null, allLabel, null)}
      {spheres.map((s) => tab(s.key, shortJobName(s.name), s.color))}
    </div>
  );
}

/** A small coloured job tag ("Rova Web") for rows and read views. */
export function JobTag({ sphere }: { sphere: Sphere | undefined }): JSX.Element | null {
  if (!sphere) return null;
  return (
    <span className="jobtag" style={sphere.color ? ({ '--job': sphere.color } as CSSProperties) : undefined}>
      {shortJobName(sphere.name)}
    </span>
  );
}

// --- Project tree --------------------------------------------------------------

/** One project in tree order with its nesting depth (0 = top level). */
export interface ProjectNode {
  project: Project;
  depth: number;
}

/**
 * Projects in tree order: each top-level project followed by its sub-projects
 * (recursively), keeping the input order among siblings. A sub-project whose
 * parent is not in the list is treated as top level, so filtering never hides it.
 */
export function projectTree(projects: readonly Project[]): ProjectNode[] {
  const ids = new Set(projects.map((p) => p.id));
  const kids = new Map<string, Project[]>();
  const roots: Project[] = [];
  for (const p of projects) {
    if (p.parentId && ids.has(p.parentId) && p.parentId !== p.id) kids.set(p.parentId, [...(kids.get(p.parentId) ?? []), p]);
    else roots.push(p);
  }
  const out: ProjectNode[] = [];
  const seen = new Set<string>();
  const walk = (p: Project, depth: number): void => {
    if (seen.has(p.id)) return; // a parent cycle in bad data must not loop forever
    seen.add(p.id);
    out.push({ project: p, depth });
    for (const c of kids.get(p.id) ?? []) walk(c, depth + 1);
  };
  for (const r of roots) walk(r, 0);
  // Anything left over sits in a cycle; show it flat rather than lose it.
  for (const p of projects) if (!seen.has(p.id)) walk(p, 0);
  return out;
}

/** 'Ferguson's Garage Doors › Website' — the project's name under its parents. */
export function projectPath(byId: ReadonlyMap<string, Project>, id: string): string {
  const names: string[] = [];
  const seen = new Set<string>();
  let cur = byId.get(id);
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    names.unshift(cur.name);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return names.join(' › ');
}

/** A list of rows split into project groups, in tree order. */
export interface ProjectGroup<T> {
  /** Project id, or null for rows with no project. */
  projectId: string | null;
  /** Header: the project's path ('Parent › Sub'), or 'No project'. */
  name: string;
  depth: number;
  rows: T[];
}

/**
 * Groups rows by project (sub-projects right under their parent), keeping the
 * incoming order within a group. Top-level trees are ordered by their first
 * row, so a ranked list stays ranked at the group level. 'No project' is last.
 */
export function groupByProject<T extends { projectId: string | null }>(
  rows: readonly T[],
  projects: readonly Project[],
): ProjectGroup<T>[] {
  const byId = new Map(projects.map((p) => [p.id, p]));
  const own = new Map<string, T[]>();
  const first = new Map<string, number>();
  const none: T[] = [];
  rows.forEach((r, i) => {
    // An unknown project (list not loaded yet, or archived away) reads as no project, never as a nameless header.
    if (!r.projectId || !byId.has(r.projectId)) {
      none.push(r);
      return;
    }
    own.set(r.projectId, [...(own.get(r.projectId) ?? []), r]);
    // Credit the first index to the project and every ancestor, for ordering.
    const seen = new Set<string>();
    let id: string | null = r.projectId;
    while (id && !seen.has(id)) {
      seen.add(id);
      if (!first.has(id)) first.set(id, i);
      id = byId.get(id)?.parentId ?? null;
    }
  });
  // The needed part of the tree: projects with rows plus their ancestors.
  const needed = [...first.keys()].flatMap((id) => byId.get(id) ?? []);
  needed.sort((a, b) => first.get(a.id)! - first.get(b.id)!);
  const groups: ProjectGroup<T>[] = [];
  for (const { project, depth } of projectTree(needed)) {
    const mine = own.get(project.id);
    if (!mine) continue; // an ancestor with no rows of its own: its children carry its name
    groups.push({ projectId: project.id, name: projectPath(byId, project.id), depth, rows: mine });
  }
  if (none.length > 0) groups.push({ projectId: null, name: 'No project', depth: 0, rows: none });
  return groups;
}
